import {useMemo} from 'react'
import type {BaseAreaChartProps, AreaChartProps} from '../types'
import {
  getDataWithDefaults,
  getTransformedPlotOptions,
  getXAxisOptionWithDefaults,
  getYAxisOptionsWithDefaults,
  isDev,
} from './shared'
import Chart from '../Chart'

const MAX_NUMBER_OF_SERIES_ITEMS = 5

export const BaseAreaChart = <T extends 'area' | 'areaspline'>({
  type,
  options,
  series: data,
  stacking = 'normal',
  marker,
  labels,
  ...rest
}: BaseAreaChartProps<T>) => {
  const {xAxis, yAxis, plot} = options ?? {}
  const xyAxisOptionsTransformed = useMemo(() => getXAxisOptionWithDefaults(xAxis), [xAxis])
  const yAxisOptionsTransformed = useMemo(() => getYAxisOptionsWithDefaults(yAxis), [yAxis])
  const series = useMemo(() => getDataWithDefaults(data as Highcharts.SeriesOptionsType[], type), [data, type])
  const plotOptionsTransformed = useMemo(
    () => getTransformedPlotOptions({plotOptions: plot, marker, labels, stacking}),
    [marker, labels, stacking, plot],
  )

  if (series.length > MAX_NUMBER_OF_SERIES_ITEMS && isDev()) {
    // eslint-disable-next-line no-console
    console.error('Ideally, an area chart should contain no more than 5 lines.')
  }

  return (
    <Chart
      type={type}
      xAxisOptions={xyAxisOptionsTransformed}
      yAxisOptions={yAxisOptionsTransformed}
      series={series as Highcharts.SeriesOptionsType[]}
      plotOptions={plotOptionsTransformed}
      {...rest}
    />
  )
}

const AreaChart = (props: AreaChartProps) => {
  return <BaseAreaChart {...props} type="area" />
}

export default AreaChart

try{ BaseAreaChart.displayName ||= 'BaseAreaChart' } catch {}
try{ AreaChart.displayName ||= 'AreaChart' } catch {}